import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Select } from "@/pages/Projects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RecordHistory } from "@/components/RecordHistory";
import { toast } from "sonner";

/**
 * Edits one existing record against a field spec. The PUT bodies on the API are whole models,
 * so the record is merged with the edited values rather than sent as a patch.
 */
export const RecordEditDialog = ({ spec, record, slug, historyCollection, onClose, onSaved }) => {
  const [form, setForm] = useState(record || {});

  useEffect(() => setForm(record || {}), [record]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const missing = spec.fields.find((f) => f.required && !String(form[f.key] ?? "").trim());
    if (missing) return toast.error(`${missing.label} is required`);
    const payload = { ...record };
    spec.fields.forEach((f) => {
      const v = form[f.key];
      if (f.type === "number") payload[f.key] = v === "" || v === null || v === undefined ? null : Number(v);
      else if (f.type === "checkbox") payload[f.key] = !!v;
      else payload[f.key] = v === "" ? null : v;
    });
    try {
      await api.put(`${spec.updatePath}/${record.id}`, payload);
      toast.success(`${spec.label} updated`);
      onClose();
      await onSaved();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={!!record} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`${slug}-edit-dialog`} className="max-h-[88vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit {spec.label}</DialogTitle>
          <DialogDescription>
            {spec.note || `Change any field on this ${spec.label.toLowerCase()} and save.`}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {spec.fields.map((f) => (
            <div key={f.key} className={f.span === 2 ? "sm:col-span-2" : ""}>
              {f.type === "checkbox" ? (
                <label className="flex items-center gap-2 pt-5 text-sm text-slate-700">
                  <input
                    data-testid={`${slug}-edit-${f.key}-checkbox`}
                    type="checkbox"
                    className="h-4 w-4"
                    checked={!!form[f.key]}
                    onChange={(e) => set(f.key)(e.target.checked)}
                  />
                  {f.label}
                </label>
              ) : (
                <>
                  <label className="field-label">{f.label}{f.required ? " *" : ""}</label>
                  {f.type === "select" ? (
                    <Select testId={`${slug}-edit-${f.key}-select`} value={form[f.key] || ""} onChange={set(f.key)} options={f.options || []} placeholder="Select" />
                  ) : f.type === "textarea" ? (
                    <Textarea data-testid={`${slug}-edit-${f.key}-input`} rows={2} value={form[f.key] || ""} onChange={(e) => set(f.key)(e.target.value)} />
                  ) : (
                    <Input
                      data-testid={`${slug}-edit-${f.key}-input`}
                      type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                      value={form[f.key] ?? ""}
                      onChange={(e) => set(f.key)(e.target.value)}
                    />
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        {historyCollection && (
          <RecordHistory
            collection={historyCollection}
            recordId={record.id}
            onReverted={async () => { onClose(); await onSaved(); }}
          />
        )}
        <DialogFooter>
          <Button variant="outline" data-testid={`${slug}-edit-cancel-btn`} onClick={onClose}>Cancel</Button>
          <Button data-testid={`${slug}-edit-save-btn`} onClick={save} style={{ backgroundColor: "var(--brand-primary)" }}>Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
